import { Injectable, inject } from '@angular/core';
import { catchError, tap } from 'rxjs/operators';
import { Action, State, StateContext } from '@ngxs/store';
import { FamilyBenefit } from '@sales/models/families/family-benefit.model';
import { Family } from '@sales/models/families/family.model';
import { FamilyService } from '@sales/services/family.service';
import { NotificationService } from '@core/services/notification.service';
import {
  ApiGetFamilyAction,
  ApiGetFamilyFailedAction,
  ApiGetFamilySuccessAction,
  ApiGetFamilyBenefitsAction,
  ApiGetFamilyBenefitsSuccessAction,
  ApiGetFamilyBenefitsFailedAction,
  ResetFamilyStateAction,
} from './family.actions';

enum Messages {
  GetFamilyFailed = 'Feil ved henting av familie',
  GetFamilyBenefitsFailed = 'Feil ved henting av familiefordeler',
}

export interface FamilyStateModel {
  family: Family;
  benefits: FamilyBenefit[];
  isGettingFamily: boolean;
  isGettingFamilyBenefits: boolean;
}

const initialFamilyState: FamilyStateModel = {
  family: {
    createdDate: null,
    dissolvedDate: null,
    familyId: '',
    naviCustomerNo: '',
    isActive: false,
    isOnboarding: '',
  } as Family,
  benefits: [],
  isGettingFamily: false,
  isGettingFamilyBenefits: false,
};

@State<FamilyStateModel>({
  name: 'family',
  defaults: initialFamilyState,
})
@Injectable()
export class FamilyState {
  private familyService = inject(FamilyService);
  private notificationService = inject(NotificationService);

  @Action(ApiGetFamilyAction)
  public getFamily({ patchState, dispatch }: StateContext<FamilyStateModel>, { customerId }: ApiGetFamilyAction) {
    patchState({ isGettingFamily: true });
    return this.familyService.getFamily(customerId).pipe(
      tap(familyResponse => dispatch(new ApiGetFamilySuccessAction(familyResponse))),
      catchError(() => dispatch(new ApiGetFamilyFailedAction()))
    );
  }

  @Action(ApiGetFamilySuccessAction)
  public getFamilySuccess({ dispatch, patchState }: StateContext<FamilyStateModel>, { family }: ApiGetFamilySuccessAction) {
    patchState({ family: family, isGettingFamily: false, isGettingFamilyBenefits: false });

    if (family && family.familyId) {
      return dispatch(new ApiGetFamilyBenefitsAction(family.familyId));
    }
  }

  @Action(ApiGetFamilyFailedAction)
  public getFamilyFailed({ patchState }: StateContext<FamilyStateModel>) {
    patchState({ isGettingFamily: false, isGettingFamilyBenefits: false });
    this.notificationService.error(Messages.GetFamilyFailed);
  }

  @Action(ApiGetFamilyBenefitsAction)
  public getFamilyBenefits(
    { patchState, dispatch }: StateContext<FamilyStateModel>,
    { familyId }: ApiGetFamilyBenefitsAction
  ) {
    patchState({ isGettingFamilyBenefits: true });
    return this.familyService.getFamilyBenefits(familyId).pipe(
      tap(familyResponse => dispatch(new ApiGetFamilyBenefitsSuccessAction(familyResponse))),
      catchError(() => dispatch(new ApiGetFamilyFailedAction()))
    );
  }

  @Action(ApiGetFamilyBenefitsSuccessAction)
  public getFamilyBenefitsSuccess(
    { patchState }: StateContext<FamilyStateModel>,
    { benefits }: ApiGetFamilyBenefitsSuccessAction
  ) {
    if (benefits) {
      patchState({ benefits: benefits, isGettingFamilyBenefits: false });
    }
  }

  @Action(ApiGetFamilyBenefitsFailedAction)
  public getFamilyBenefitsFailed({ patchState }: StateContext<FamilyStateModel>) {
    patchState({ isGettingFamilyBenefits: false });
    this.notificationService.error(Messages.GetFamilyBenefitsFailed);
  }

  @Action(ResetFamilyStateAction)
  public resetFamilyState({ setState }: StateContext<FamilyStateModel>) {
    setState(initialFamilyState);
  }
}
